import {
  PAYROLL_ROSTER_FIELD_TYPE,
  type PayrollRosterFilterField,
  type PayrollRosterDraftMember,
  type PayrollRosterField,
  type RosterFieldValueSummary,
} from "@/payroll/roster/types";
import { isBlankRosterValue } from "@/payroll/roster/utils/members";
import { rosterValueKey } from "./filter-matching";

export const summarizeRosterFieldValues = (
  drafts: Pick<PayrollRosterDraftMember, "member">[],
  fields: PayrollRosterField[],
): Record<string, RosterFieldValueSummary> =>
  Object.fromEntries(
    fields.map((field) => {
      const summary: RosterFieldValueSummary = {
        counts: {},
        blank: 0,
        min: null,
        max: null,
      };

      drafts.forEach(({ member }) => {
        const value = member.field_values[field.key];
        if (isBlankRosterValue(value)) {
          summary.blank += 1;
          return;
        }

        const key = rosterValueKey(value, field);
        summary.counts[key] = (summary.counts[key] ?? 0) + 1;

        const numeric = Number(value);
        if (
          field.field_type === PAYROLL_ROSTER_FIELD_TYPE.number &&
          Number.isFinite(numeric)
        ) {
          summary.min =
            summary.min === null ? numeric : Math.min(summary.min, numeric);
          summary.max =
            summary.max === null ? numeric : Math.max(summary.max, numeric);
        }
      });

      return [field.key, summary];
    }),
  );

export const rosterFilterOptions = (
  contractOptions: string[],
  summary: RosterFieldValueSummary | undefined,
): string[] => {
  const extras = Object.keys(summary?.counts ?? {})
    .filter((value) => !contractOptions.includes(value))
    .sort((left, right) => left.localeCompare(right));
  return [...contractOptions, ...extras];
};

export const buildPayrollRosterFilterFields = (
  drafts: Pick<PayrollRosterDraftMember, "member">[],
  fields: PayrollRosterField[],
  fieldOptions: Record<string, string[]>,
): PayrollRosterFilterField[] => {
  const summaries = summarizeRosterFieldValues(drafts, fields);

  return fields.map((field) => ({
    field,
    options: rosterFilterOptions(
      fieldOptions[field.key] ?? field.options ?? [],
      summaries[field.key],
    ),
    summary: summaries[field.key],
  }));
};
